'use client';

import React, { useState, useEffect, useRef, useContext, Suspense, useReducer, useCallback, useMemo } from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import type { ButtonBackground, ButtonColor, ButtonSize, ButtonStyle } from '@tranquility-ai/theme-components/button';
import { Combobox } from '@tranquility-ai/theme-components/combobox';
import type { ComboboxOption } from '@tranquility-ai/theme-components/combobox';
import '@tranquility-ai/theme-components/combobox.css';
import { TpTooltip } from "@tranquility-ai/theme-components/tooltip";
import deepEqual from 'deep-equal';
import { ArrowLeftIcon, ArrowRightIcon, MagicWandIcon, XCircleIcon, EraserIcon, XIcon, BookmarksSimpleIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { DatePicker } from "@heroui/date-picker";
import { TimeInput } from "@heroui/date-input";
// Typechecks once @tranquility-ai/theme-components 3.9.0 is pinned.
import { Input, Checkbox } from '@tranquility-ai/theme-components';
import { Popover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';
import { Time } from "@internationalized/date";
import { CalendarDate } from "@internationalized/date";
import { useDateFormatter } from "@react-aria/i18n";
import { readStoredLocale } from '@/i18n/LocaleProvider';
import { responseLanguageForLocale } from '@/i18n/locales';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { Slide, MediaSource, EvidenceObject, Source } from '@/shared/types';
import { dedupeSources, dedupeMediaSources, getDescriptionFromAnswerText } from '@/shared/clientFunctions';
import { EventFromChat } from './answer-item';
import {
  extractPathAfterBulk,
  getClientTimeFromStr,
  getDateFromDateStr,
  getTimeFromStr,
  getMediaRowDisplayType,
  getTimezoneList,
  FetchWithVersionCheck,
  getBestMediaFromSources,
  CaptureError,
  getTokenInfo,
  getUserEmailForHeaders,
  getUiSourceFromChatSource,
  getCaseInternalName,
} from '@/shared/functions';
import { resolveCellIdFromIdToken } from '@/shared/cell-session';
import { ensureCitationSupInlineStyles } from '@/shared/citationSupBadge';
import { fetchChatHistory, postAssistantChat } from '@/shared/cors-assistant-api';
import { useCaseFolderName, caseFolderNameBody } from '@/hooks/useCaseFolderName';

import { Modal } from '@tranquility-ai/theme-components/modal';
import '@tranquility-ai/theme-components/modal.css';
import { DismissibleBlurOverlay } from './dismissible-blur-overlay';
import { Spinner } from '@heroui/spinner';
import UnifiedSources, { syncPairedSourceContextsFromMedia } from './cells/unifiedSources';
import { HotKeys } from '@/components/hot-keys';
import { FullScreenContext, ToastContext, useReactiveAuthTokens, useReactiveFeatureFlags } from '@/app/contexts';
import MediaDisplay from './ui/mediaDisplay';
import { isDisplayableMediaType, ENRICHMENT_POLL_INTERVAL_MS, ENRICHMENT_POLL_TIMEOUT_MS, isEnrichmentHistoryPending } from '@/shared/constants';
import { isJsonMediaEnabled } from '@/shared/media-player-transition';
import dynamic from 'next/dynamic';
import { ToastLevel } from './ui/toast';
import { useToolsDrawer } from './tools-drawer';
import { normalizeTimelineEventStillFrames, stripStillFrameThumbnailFromSource } from '@/shared/timeline-event-payload';
import {
  formIsDirtyAgainstBaseline,
  formSnapshotForUnsavedCheck,
  hasUnsavedChanges,
  mergeLiveMediaIntoForm,
  nextHydrationBaseline,
} from './eventModalUnsavedChanges';
import './eventModal.markdown-editor.css';

const MarkdownEditor = dynamic(
  () => import('@tranquility-ai/theme-components/markdown-editor').then((m) => m.MarkdownEditor),
  { ssr: false }
);

/**
 * Shared chrome for the Date / Time / Timezone clear buttons so all three (x)
 * controls read identically. Vertical alignment is left to the row rather than
 * baked in here: `items-end` where the control renders its own label above the
 * input (DatePicker, TimeInput), `items-center` where the label is a separate
 * sibling above the row (Timezone).
 */
const clearFieldButtonProps = {
  type: 'button' as const,
  iconOnly: true,
  size: 'md' as ButtonSize,
  style: 'text' as ButtonStyle,
  color: 'light' as ButtonColor,
  background: 'inverse' as ButtonBackground,
};

/** Matches the `md` icon slot, which the Button clamps `--tp-button-icon-size` to. */
const CLEAR_FIELD_ICON_SIZE = 20;

/**
 * Shared chrome for every control in the modal's 36px header bar — the title's
 * paging arrows and the trailing Enrich / Undo / Favorites controls. The Figma
 * ".PaneControls" row draws these as borderless controls on the ink/075 bar, so
 * they are `text` style rather than `outline`. Labelled buttons keep the theme
 * 4px radius; `iconOnly` still uses the Button's pill radius.
 */
const headerControlButtonProps = {
  type: 'button' as const,
  size: 'sm' as ButtonSize,
  style: 'text' as ButtonStyle,
  color: 'light' as ButtonColor,
  background: 'inverse' as ButtonBackground,
};

/** Matches the `sm` icon slot, which the Button clamps `--tp-button-icon-size` to. */
const HEADER_ICON_SIZE = 16;

/**
 * Date / Time field chrome aligned with theme Input on an inverse surface
 * (white field, 8px radius, 42px, 16px ink). HeroUI still owns the picker.
 * `inverse-form-surface` on the field base supplies the color tokens.
 */
const comboboxAlignedFieldClassNames = {
  label: 'text-white/100 font-light tracking-wider text-[14px]',
  inputWrapper:
    'box-border h-[42px] min-h-[42px] rounded-[8px] bg-white px-4 shadow-none border border-solid border-transparent data-[hover=true]:bg-white group-data-[focus=true]:bg-white group-data-[focus=true]:outline group-data-[focus=true]:outline-2 group-data-[focus=true]:outline-offset-1 group-data-[focus=true]:outline-[var(--color-border-focus)]',
  innerWrapper: 'text-[var(--color-text-primary)]',
  input: 'text-base text-[var(--color-text-primary)]',
  segment:
    'text-base text-[var(--color-text-primary)] data-[editable=true]:text-[var(--color-text-primary)] data-[placeholder=true]:text-[var(--color-text-placeholder)]',
};

type FormData = {
  id?: string | null | undefined;
  displayOrder?: number;
  tags?: string[];
  slideTitle?: string;
  slideSubTitle?: string;
  slideDescription?: string;
  courtroomTitle?: string;
  courtroomDescription?: string;
  media?: MediaSource[];
  show?: boolean;
  hideFromTimeline?: boolean;
  date?: string | null;
  time?: string | null;
  isTimeValid?: boolean;
  eventLocalTimezone?: string | null;
  sources?: MediaSource[];
  displayDate?: string | null;
  eventType?: string | null;
  isDirty?: boolean;
};

export type EventSaveResult = {
  success: boolean;
  event?: Slide;
  /** Raw timeline-service event body; used to patch only fields the API actually returned. */
  serverRecord?: Record<string, unknown>;
};

type EventSaveStatus = 'idle' | 'saving' | 'success' | 'error';

const FORM_SERVER_SYNC_KEYS = [
  'slideTitle',
  'slideSubTitle',
  'slideDescription',
  'courtroomTitle',
  'courtroomDescription',
  'sources',
  'media',
  'date',
  'displayDate',
  'time',
  'isTimeValid',
  'eventLocalTimezone',
  'eventType',
  'show',
  'hideFromTimeline',
  'displayOrder',
] as const;

type FormServerSyncKey = (typeof FORM_SERVER_SYNC_KEYS)[number];

const slideToFormSyncPatch = (slide: Slide): Partial<FormData> => ({
  slideTitle: slide.slideTitle,
  slideSubTitle: slide.slideSubTitle,
  slideDescription: slide.slideDescription,
  courtroomTitle: slide.courtroomTitle,
  courtroomDescription: slide.courtroomDescription,
  sources: slide.sources as MediaSource[] | undefined,
  media: slide.media as MediaSource[] | undefined,
  date: slide.date ?? null,
  displayDate: slide.displayDate ?? null,
  time: slide.time ?? null,
  isTimeValid: slide.isTimeValid === true,
  eventLocalTimezone: slide.eventLocalTimezone ?? null,
  eventType: slide.eventType ?? null,
  show: slide.show ?? false,
  hideFromTimeline: slide.hideFromTimeline === true,
  displayOrder: slide.displayOrder,
});

const recordHasKey = (record: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

const serverRecordTouchesFormKey = (
  record: Record<string, unknown>,
  formKey: FormServerSyncKey,
): boolean => {
  switch (formKey) {
    case 'date':
    case 'displayDate':
    case 'time':
      return recordHasKey(record, 'eventDateTime');
    case 'isTimeValid':
      return recordHasKey(record, 'isTimeValid') || recordHasKey(record, 'eventDateTime');
    case 'eventLocalTimezone':
      return recordHasKey(record, 'eventLocalTimezone');
    case 'show':
      return recordHasKey(record, 'hidden');
    case 'hideFromTimeline':
      return recordHasKey(record, 'hiddenFromTimeline');
    case 'media':
      if (recordHasKey(record, 'media')) {
        return Array.isArray(record.media);
      }
      return Boolean(record.mediaType) && Boolean(record.mediaUrl);
    case 'sources':
      return recordHasKey(record, 'sources') && Array.isArray(record.sources);
    default:
      return recordHasKey(record, formKey);
  }
};

/** Only map API fields that were present on the PUT response onto form sync keys. */
const buildPartialServerFormPatch = (
  serverRecord: Record<string, unknown>,
  mappedSlide: Slide,
): Partial<FormData> => {
  const fullPatch = slideToFormSyncPatch(mappedSlide);
  const patch: Partial<FormData> = {};
  for (const key of FORM_SERVER_SYNC_KEYS) {
    if (serverRecordTouchesFormKey(serverRecord, key)) {
      (patch as Record<string, unknown>)[key] = fullPatch[key];
    }
  }
  return patch;
};


type EnrichmentApiResponse = {
  output?: string;
  citations?: Source[];
};

type EnrichmentResult = {
  slideDescription: string;
  sources: MediaSource[];
  media: MediaSource[];
};

export type EnrichEventOptions = {
  useEventSourcesAsScope?: boolean;
  keepCurrentMedia?: boolean;
};

export const getEventSourceUrlsForScope = (sources?: MediaSource[]) => {
  return (sources ?? [])
    .map((source) => source.sourceUrl)
    .filter((sourceUrl): sourceUrl is string => Boolean(sourceUrl && sourceUrl.trim() !== ''));
};

export const mergeEnrichmentMedia = (
  currentMedia: MediaSource[] = [],
  enrichedMedia: MediaSource[] = [],
  keepCurrentMedia = false,
) => {
  return dedupeMediaSources(keepCurrentMedia ? [...currentMedia, ...enrichedMedia] : [...enrichedMedia]);
};

/**
 * Computes enrichment from API response (description, sources, media).
 *
 * @param result - The API response to compute enrichment from
 * @returns The enrichment result or null if there is no output in the response
 */
export const computeEnrichmentFromApiResponse = (
  result: EnrichmentApiResponse,
  jsonMediaDisplayEnabled = false,
): EnrichmentResult | null => {
  if (!result?.output) return null;
  const answerText = ((result.output || "") as string)
    .replace(/<sup>.*?<\/sup>/g, "")
    .replace(/<\/p><p>/g, "")
    .trim();
  const citations = result.citations ?? [];
  // Enrichment feeds its description straight into the MDX (markdown) editor and
  // does not preserve an HTML snapshot, so strip any residual inline HTML tags to
  // plain text. Otherwise raw tags (e.g. color spans) would surface as literal
  // text in the editor. getDescriptionFromAnswerText already converts block-level
  // tags/<br> to newlines; this removes what's left.
  const description = getDescriptionFromAnswerText(answerText).replace(/<[^>]*>/g, '');
  const sources = citations.map((citation) =>
    getUiSourceFromChatSource(citation),
  );
  const media = getBestMediaFromSources(sources, jsonMediaDisplayEnabled);
  return {
    slideDescription: description,
    sources: Array.from(new Set(sources)) as MediaSource[],
    media,
  };
};

/**
 * Form snapshot shape used when applying enrichment.
 * All fields needed to build the full form after enrichment.
 */
type EventFormSnapshot = {
  id?: string | null;
  displayOrder?: number;
  slideTitle?: string;
  slideSubTitle?: string | undefined;
  slideDescription?: string;
  date?: string | null;
  displayDate?: string | null;
  time?: string | null;
  isTimeValid?: boolean;
  eventLocalTimezone?: string | null;
  eventType?: string | null;
  sources?: MediaSource[];
  media?: MediaSource[];
  show?: boolean;
  hideFromTimeline?: boolean;
};

/**
* Merges enrichment result into current form snapshot.
 *
 * @param currentForm - The current form snapshot to merge enrichment into
 * @param enrichment - The enrichment result to merge into the current form snapshot,
 * using currentForm.slideDescription as fallback when enrichment.slideDescription is empty
 * @returns The new form snapshot with the enrichment merged in
 */
const applyEnrichmentToForm = (
  currentForm: EventFormSnapshot,
  enrichment: EnrichmentResult,
): EventFormSnapshot => {
  return {
    ...currentForm,
    slideDescription:
      enrichment.slideDescription?.trim() !== ""
        ? enrichment.slideDescription
        : (currentForm.slideDescription ?? ""),
    sources: (currentForm.sources ??
      []) as MediaSource[],
    media: (currentForm.media ?? []) as MediaSource[],
  };
};

const normalizeFromSourceDescription = (description?: string): string => {
  if (!description) return '';
  // So if we a chat chunk happens to begin with break tags like this:
  //
  // "<br><br><br>status: Read<br>label: Default<br>platform: Unknown<br>"
  //
  // It can cause the WYSIWYG editor to break and render nothing. This seems to be
  // a known issue with the editor.
  //
  // Thus, we normalize common HTML breaks to markdown/plain newlines to
  // make sure we always have something to render in the description box
  // even if the chat chunk begins with a lot of break tags.
  return description
    .replace(/<br\s*\/?>/gi, '\n')
    .trim();
};

const getCurrentEventFromChat = (eventsFromChat: EventFromChat[], index: number): Slide | null => {
  if (!eventsFromChat || !eventsFromChat?.[index]) return null;
  const chatEventToAdd = eventsFromChat?.[index];
  if (chatEventToAdd) {
    const description = chatEventToAdd.answerText === 'FROM_SOURCE'
      ? normalizeFromSourceDescription(chatEventToAdd.slideDescription)
      : getDescriptionFromAnswerText(chatEventToAdd.answerText);
    const sourcesFromChatEvent = chatEventToAdd.sources ? chatEventToAdd.sources.map((source: Source) => {
      const sourceToUse: MediaSource = {
        sourceType: extractPathAfterBulk(source.sourceFilePath || '').sourceType || 'Unknown',
        sourceUrl: source.sourceFilePath,
      }
      if (source.sourceContext) {
        sourceToUse.sourceContext = source.sourceContext;
      }
      if (source.sourceContext?.pageNumber || source.pageNumber) {
        sourceToUse.pageNumber = source.sourceContext?.pageNumber || source.pageNumber;
      }
      return sourceToUse;
    }) : [] as MediaSource[];
    const sourcesForEvents = dedupeSources(sourcesFromChatEvent);
    const newEvent = {
      id: 'NEW_EVENT_FROM_CHAT_0',
      slideTitle: chatEventToAdd.answerText === 'FROM_SOURCE' ? 'FROM SOURCE' : '',
      slideDescription: description,
      show: true,
      approval: 'approved',
      sources: sourcesForEvents,
      media: chatEventToAdd.media as MediaSource[] || [],
    }
    return newEvent;
  }
  return null;
}

const timezoneComboboxOptions: ComboboxOption[] = (getTimezoneList() || []).map((tzObj) => ({
  value: tzObj.name,
  label: `${tzObj.name} (${tzObj.offset})`,
}));

/**
 * Gets the initial form data for the event modal.
 *
 * @param params - The parameters to get the initial form data from
 * @returns The initial form data or null if there is no initial form data
 */
function getInitialFormData(params: {
  localColumn: string | null | undefined;
  eventsFromChat: EventFromChat[] | undefined;
  events: Slide[] | undefined;
  currentEventIndex: number | null;
  mediaSourcesFromChat: MediaSource[] | undefined;
  sourcesFromChat: MediaSource[] | undefined;
  enriching: boolean;
  currentFormRefValue: FormData | null;
  preEnrichEventValue: FormData | null;
  jsonMediaDisplayEnabled?: boolean;
}): FormData | null {
  const {
    localColumn,
    eventsFromChat,
    events,
    currentEventIndex,
    mediaSourcesFromChat,
    sourcesFromChat,
    enriching,
    currentFormRefValue,
    preEnrichEventValue,
    jsonMediaDisplayEnabled = false,
  } = params;

  if (localColumn === "newEventFromChat" && eventsFromChat && !enriching) {
    const currentEventFromChat = getCurrentEventFromChat(eventsFromChat, 0);
    if (!currentEventFromChat) return null;
    let mediaItemFromChat = currentEventFromChat.media || [];
    if (mediaItemFromChat.length === 0) {
      if (currentEventFromChat.slideTitle === "FROM SOURCE") {
        mediaItemFromChat =
          getBestMediaFromSources(
            currentEventFromChat.sources as MediaSource[],
            jsonMediaDisplayEnabled,
          ) || [];
        currentEventFromChat.slideTitle = "";
      } else {
        mediaItemFromChat =
          getBestMediaFromSources(mediaSourcesFromChat || [], jsonMediaDisplayEnabled) || [];
      }
      currentEventFromChat.media = mediaItemFromChat as MediaSource[];
    }
    let combinedSourcesFromChat = (currentEventFromChat.sources ||
      []) as MediaSource[];
    if (currentEventFromChat.media) {
      // Match unifiedSources media→source mapping: content-only JSON has no
      // mediaUrl, so identity must come from sourceUrl/sourceFilePath or the
      // row is dropped by the empty-sourceUrl filter below.
      const sourcesFromMedia = currentEventFromChat.media.map(
        (mediaItem: MediaSource) => {
          const hasMediaUrl = !!(mediaItem.mediaUrl || mediaItem.mediaSrc);
          const resolvedType = getMediaRowDisplayType(mediaItem, mediaItem.fileInfo);
          const identityUrl =
            mediaItem.sourceUrl ||
            mediaItem.sourceFilePath ||
            mediaItem.mediaUrl ||
            mediaItem.mediaSrc;
          return {
            sourceUrl: identityUrl,
            sourceFilePath: mediaItem.sourceFilePath || identityUrl,
            sourceType: resolvedType || mediaItem.mediaType,
            mediaType: resolvedType || mediaItem.mediaType,
            ...(hasMediaUrl
              ? {
                  mediaUrl: mediaItem.mediaUrl || mediaItem.mediaSrc,
                  mediaSrc: mediaItem.mediaSrc || mediaItem.mediaUrl,
                }
              : {}),
            content: mediaItem.content,
            sourceContext: mediaItem.mediaContext || mediaItem.sourceContext,
            fileInfo: mediaItem.fileInfo,
          } as MediaSource;
        },
      );
      combinedSourcesFromChat =
        combinedSourcesFromChat.concat(sourcesFromMedia);
    }
    if (combinedSourcesFromChat?.length === 1) {
      if (currentEventFromChat.media?.length === 0) {
        currentEventFromChat.media = getBestMediaFromSources(
          currentEventFromChat.sources as MediaSource[],
          jsonMediaDisplayEnabled,
        );
      }
      currentEventFromChat.slideTitle = "";
    }
    currentEventFromChat.sources = dedupeSources(
      combinedSourcesFromChat,
    ).filter(
      (sourceTest: MediaSource) =>
        sourceTest.sourceUrl != null &&
        sourceTest.sourceUrl !== undefined &&
        sourceTest.sourceUrl.trim() !== "",
    );
    return { ...currentEventFromChat, isTimeValid: true } as FormData;
  }

  if (localColumn === "duplicate" && !enriching && currentEventIndex !== null) {
    const duplicateEvent = {
      ...events?.[currentEventIndex],
      show: true,
    } as Slide & { id?: string };
    delete duplicateEvent.id;
    return duplicateEvent as FormData;
  }

  if (localColumn === "create" && !enriching) {
    const mediaItemFromChat = mediaSourcesFromChat?.[0];
    const useable =
      mediaItemFromChat &&
      isDisplayableMediaType(
        getMediaRowDisplayType(mediaItemFromChat, mediaItemFromChat.fileInfo),
        { jsonMediaDisplay: jsonMediaDisplayEnabled },
      );
    const media = useable ? [mediaItemFromChat] : [];
    return {
      id: "NEW_EVENT_FROM_TABLE",
      slideTitle: "",
      slideSubTitle: undefined,
      slideDescription: "",
      show: true,
      sources: dedupeSources(sourcesFromChat || []) || [],
      media: media || [],
      isTimeValid: true,
    };
  }

  if (
    currentEventIndex !== null &&
    ((events && events?.[currentEventIndex]) || enriching) &&
    !currentFormRefValue
  ) {
    const currentEvent = preEnrichEventValue ?? events?.[currentEventIndex];
    let descriptionForForm = currentEvent?.slideDescription || "";
    if (descriptionForForm.startsWith("```")) {
      descriptionForForm = descriptionForForm.replace("```", "");
      if (descriptionForForm.startsWith("json ")) {
        descriptionForForm = descriptionForForm.replace("json ", "");
      }
    }
    return {
      id: currentEvent?.id || null,
      displayOrder: currentEvent?.displayOrder || -1,
      date: currentEvent?.date ? currentEvent.date : null,
      displayDate: currentEvent?.displayDate
        ? currentEvent.displayDate
        : currentEvent?.date || null,
      time: currentEvent?.time ? currentEvent.time : null,
      isTimeValid: currentEvent?.isTimeValid === true ? true : false,
      eventLocalTimezone: currentEvent?.eventLocalTimezone || null,
      slideTitle: currentEvent?.slideTitle || "",
      slideSubTitle: currentEvent?.slideSubTitle || undefined,
      slideDescription: descriptionForForm,
      courtroomTitle: currentEvent?.courtroomTitle || "",
      courtroomDescription: currentEvent?.courtroomDescription || "",
      eventType: currentEvent?.eventType || "",
      sources: (currentEvent?.sources || []) as MediaSource[],
      media: (currentEvent?.media || []) as MediaSource[],
      show: currentEvent?.show ?? false,
      hideFromTimeline: currentEvent?.hideFromTimeline === true,
    };
  }

  if (!currentFormRefValue && !enriching) {
    return {
      id: "NEW",
      slideTitle: "",
      slideDescription: "",
      slideSubTitle: undefined,
      show: true,
      isTimeValid: true,
    };
  }

  if (localColumn === "unifiedSourcesChanges" && currentFormRefValue) {
    return { ...currentFormRefValue };
  }

  return null;
}

const NEW_EVENT_COLUMNS = ['create', 'duplicate', 'newEventFromChat'];

/**
 * Finds the array position of an event by its stable `id`.
 *
 * The modal looks events up positionally, but `events` (allSlides) is
 * re-densified/re-sorted on every refetch (getSortedSlides), so a held index
 * can point at the wrong slide — or none at all. Resolving by id lets us
 * recover the correct position after a refetch/re-sort.
 *
 * @returns the index of the matching event, or -1 if not found
 */
const findEventIndexById = (
  slides: Slide[] | undefined,
  id: string | null | undefined,
): number => {
  if (!slides || id == null) return -1;
  return slides.findIndex((slide) => slide?.id === id);
};

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddEvent: (newEvent: Slide) => Promise<EventSaveResult>;
  onEditEvent: (editedEvent: Slide) => Promise<EventSaveResult>;
  column?: string | null;
  events?: Slide[];
  eventIndex: number | null;
  isLegacy: boolean;
  editingEnabled: boolean;
  sourcesFromChat?: MediaSource[];
  mediaSourcesFromChat?: MediaSource[];
  eventsFromChat?: EventFromChat[];
  availableEvidence?: EvidenceObject[];
  caseId?: string;
}

// NOTE -- Jake Mason | (2026-02-03)
// Maybe overkill, but I prefer to use enums whenever possible even if it's just for a small set like these
enum FormReducerActionType {
  SetForm = "SET_FORM",
  SetField = "SET_FIELD",
}

type FormReducerAction =
  | { type: FormReducerActionType.SetForm; payload: FormData | undefined }
  | { type: FormReducerActionType.SetField; payload: Partial<FormData> };

function formReducer(
  state: FormData | undefined,
  action: FormReducerAction,
): FormData | undefined {
  switch (action.type) {
    case FormReducerActionType.SetForm:
      return action.payload;
    case FormReducerActionType.SetField:
      return { ...(state ?? {}), ...action.payload } as FormData;
    default:
      return state;
  }
}

// NOTE -- Jake Mason | (2026-02-04):
// This is a memoized component that isolates the sources/media state so changes don't re-render the whole modal.
// It is memoized so the parent re-renders (title, date, etc.) don't re-render this or UnifiedSources.
const EventModalSourcesSection = React.memo(function EventModalSourcesSection({
  eventKey,
  initialSources,
  initialMedia,
  onSourcesChange,
  ...unifiedSourcesProps
}: {
  eventKey: number | string;
  initialSources?: MediaSource[];
  initialMedia?: MediaSource[];
  onSourcesChange: React.ComponentProps<typeof UnifiedSources>['handleUnifiedSourcesChanges'];
} & Omit<React.ComponentProps<typeof UnifiedSources>, 'value' | 'mediaValue' | 'handleUnifiedSourcesChanges'>) {
  const [sources, setSources] = useState<MediaSource[]>(initialSources ?? []);
  const [media, setMedia] = useState<MediaSource[]>(initialMedia ?? []);

  useEffect(() => {
    setSources(initialSources ?? []);
    setMedia(initialMedia ?? []);
  }, [eventKey, initialSources, initialMedia]);

  const handleChange = useCallback<NonNullable<React.ComponentProps<typeof UnifiedSources>['handleUnifiedSourcesChanges']>>(
    (newSources, newMedia, options) => {
      setSources(newSources);
      setMedia(newMedia);
      onSourcesChange?.(newSources, newMedia, options);
    },
    [onSourcesChange],
  );

  return (
    <UnifiedSources
      {...unifiedSourcesProps}
      value={sources}
      mediaValue={media}
      handleUnifiedSourcesChanges={handleChange}
    />
  );
});

const EventModalComponent: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onAddEvent,
  onEditEvent,
  column,
  events,
  eventIndex,
  isLegacy,
  editingEnabled,
  sourcesFromChat,
  mediaSourcesFromChat,
  eventsFromChat,
  caseId,
}) => {
  const t = useTimePilotT();
  // NOTE -- Jake Mason | (2026-02-03):
  // using a reducer here makes updating the code pure and should be much easier to test
  // instead of the previous form that was a set of state mutations and multiple setters/getters
  const [formData, dispatchForm] = useReducer(formReducer, undefined);
  // Sent alongside `selectedCase` on enrichment so retrieval filters on the
  // case's real tag rather than a `case:<display name>` guess, which matches
  // nothing for a UUID-folder case. See `useCaseFolderName`.
  const caseFolderName = useCaseFolderName(caseId);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [currentEventIndex, setCurrentEventIndex] = useState<number | null>(eventIndex);
  const [navDir, setNavDir] = useState<'left' | 'right' | 'no'>('no');
  const [markdownValue, setMarkdownValue] = useState('');
  const [courtroomMarkdownValue, setCourtroomMarkdownValue] = useState('');
  const [enrichWithScope, setEnrichWithScope] = useState(localStorage.getItem('enrichWithScope') === 'true');
  const [keepCurrentMediaOnEnrich, setKeepCurrentMediaOnEnrich] = useState(localStorage.getItem('keepCurrentMediaOnEnrich') !== 'false');
  const [enrichPopoverVisible, setEnrichPopoverVisible] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichedData, setEnrichedData] = useState<Slide | null>(null);
  const [enrichObject, setEnrichObject] = useState({
    enrichTitle: true,
    enrichDescription: true,
    enrichSources: true,
    enrichMedia: true,
  });
  const [undoEnrichEvent, setUndoEnrichEvent] = useState(false);
  const [canUndoEnrichEvent, setCanUndoEnrichEvent] = useState(false);
  const [localColumn, setLocalColumn] = useState(column);

  // Reactive so a memoized EventModal re-renders on a token/flag change while
  // skipping unrelated `Main` re-renders.
  const { idToken, accessToken, realAccessToken } = useReactiveAuthTokens();
  const featureFlags = useReactiveFeatureFlags();
  const jsonMediaDisplayEnabled = isJsonMediaEnabled(featureFlags);
  const { setToast, setToastOpen } = useContext(ToastContext);
  const { isFullscreen } = useContext(FullScreenContext) || { isFullscreen: false };
  const toolsDrawer = useToolsDrawer();
  const registerApplyTextToEventDescription =
    toolsDrawer?.registerApplyTextToEventDescription;

  const formDataRef = useRef<FormData | null>(null);
  const formDataInitial = useRef<FormData | null>(null);
  const eventsRef = useRef<Slide[]>([]);

  const timeInputRef = useRef<HTMLInputElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const currentEventIndexRef = useRef<number | null>(null);

  // Stable identity of the event currently being edited. We navigate/look up by
  // array position for the UI, but a refetch re-densifies displayOrder and may
  // re-sort the list, stranding a held index. Tracking the id lets us re-resolve
  // the correct position after `events` changes so the modal never strands.
  const currentEventIdRef = useRef<string | null>(null);
  const cardBodyScrollRef = useRef<HTMLDivElement | null>(null);
  const cardBodyScrollTopRef = useRef(0);
  /** After edit save, skip one form reinit when the timeline list refetches. */
  const suppressFormReinitOnceRef = useRef(false);
  const saveStatusShownAtRef = useRef(0);
  const saveStatusDismissCleanupRef = useRef<(() => void) | null>(null);
  const [saveStatus, setSaveStatus] = useState<EventSaveStatus>('idle');

  const preEnrichEvent = useRef<FormData | null>(null);
  // Holds the original HTML-rich description from an AI chat response so that
  // colors are preserved on save even though MDXEditor strips inline HTML.
  // Cleared by enrichment or apply-text; on save we use descriptionUserHasEditedRef
  // to detect genuine user edits.
  const rawChatDescriptionRef = useRef<string | null>(null);
  // Set to true as soon as any debounced emission arrives after the first one,
  // meaning the user has typed something. A separate ref avoids the debounce
  // race where markdownValue is still at the initial-emission value while the
  // editor already contains new text.
  const descriptionUserHasEditedRef = useRef<boolean>(false);
  // The first markdown string MDXEditor emits after loading rawChatDescriptionRef.
  // Used only to swallow that initial emission so it doesn't count as an edit.
  const mdxInitialEmissionRef = useRef<string | null>(null);

  const filteredMediaSourcesFromChat = useMemo(
    () =>
      mediaSourcesFromChat?.filter((mediaItem: MediaSource) =>
        isDisplayableMediaType(
          getMediaRowDisplayType(mediaItem, mediaItem.fileInfo),
          { jsonMediaDisplay: jsonMediaDisplayEnabled },
        ),
      ) ?? [],
    [mediaSourcesFromChat, jsonMediaDisplayEnabled],
  );

  const newEvent = (formData && formData.id === 'NEW') ||
    (formData && formData.id && formData.id.split('_')[0] === 'NEW') ||
    localColumn === 'create' ||
    localColumn === 'duplicate' ||
    localColumn === 'newEventFromChat';

  // True when we're editing a real event and the list is *currently being
  // refetched*. Some background flows blank the list entirely first
  // (setAllSlides(undefined) on the isUpdating=false paths: entity-service
  // polling, enrichment, timeline-select), and the parent only ever signals an
  // in-flight refetch by setting `events` to undefined — it never sets []. So a
  // blanked list with a tracked id is a transient loading state; the event is
  // almost certainly coming back.
  //
  // Critically, this must NOT be true once the list has *settled*: a populated
  // array (even an empty one) is a finished result, so a tracked id that is
  // absent from it means the event was genuinely removed/deleted. Gating on
  // `events === undefined` (rather than merely "id not found") is what lets the
  // settled-but-missing case fall through to the "removed / no longer
  // available" dialog instead of spinning forever.
  // Parent list is mid-refetch (``events === undefined``). Background flows —
  // notably the LaunchDarkly auto-refetch in ``main.tsx`` — call
  // ``fetchNewTimeline(..., isUpdating=false)`` which blanks ``allSlides`` before
  // repopulating it ~1s after first load. Keep the modal mounted, but do not
  // show the loading header once form state for the open event is already loaded.
  const isEventsListRefetching =
    !newEvent &&
    events === undefined &&
    (currentEventIdRef.current != null || currentEventIndex !== null);

  const loadedOpenEventForm = formDataRef.current ?? formData;
  const hasLoadedOpenEventForm =
    !!loadedOpenEventForm?.id &&
    (currentEventIdRef.current == null ||
      String(loadedOpenEventForm.id) === String(currentEventIdRef.current));

  const isResolvingOpenEvent = isEventsListRefetching;
  const showLoadingEventDetailsHeader =
    isEventsListRefetching && !hasLoadedOpenEventForm;

  // load new data if this is opened with a new eventIndex
  useEffect(() => {
    if (eventIndex !== null) {
      setCurrentEventIndex(eventIndex);
    }
  }, [eventIndex]);

  useEffect(() => {
    if (currentEventIndex === null) return;
    if (currentEventIndexRef.current !== currentEventIndex) {
      formDataRef.current = null;
      preEnrichEvent.current = null;
      currentEventIndexRef.current = currentEventIndex;
    }
  }, [events, currentEventIndex]);

  // Keep `currentEventIndex` aligned with the event being edited (by id) after a
  // refetch re-densifies/re-sorts `events`. Without this, a date/time edit that
  // re-sorts the list while the modal is open leaves the held index pointing at
  // a different — or missing — slide, which surfaces the "Event unavailable"
  // dialog even though the event still exists.
  useEffect(() => {
    if (newEvent) return;
    const trackedId = currentEventIdRef.current;
    if (trackedId == null) return;
    const resolvedIndex = findEventIndexById(events, trackedId);
    if (resolvedIndex !== -1 && resolvedIndex !== currentEventIndex) {
      setCurrentEventIndex(resolvedIndex);
    }
  }, [events, currentEventIndex, newEvent]);

  const onTimezoneChange = (value: string | undefined) => {
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: { eventLocalTimezone: value ?? null, isDirty: true },
    });
  }

  const handleEnrichWithScopeChange = (isSelected: boolean) => {
    setEnrichWithScope(isSelected);
    localStorage.setItem('enrichWithScope', isSelected.toString());
  }

  const handleKeepCurrentMediaOnEnrichChange = (isSelected: boolean) => {
    setKeepCurrentMediaOnEnrich(isSelected);
    localStorage.setItem('keepCurrentMediaOnEnrich', isSelected.toString());
  }

  // Keep ref in sync for async handlers (e.g. enrichEvent) that need latest form
  useEffect(() => {
    formDataRef.current = formData ?? null;
  }, [formData]);

  const handleDateChange = (date: CalendarDate | null) => {
    if (date) {
      const dateStr = `${date.year}-${date.month}-${date.day}`;
      dispatchForm({ type: FormReducerActionType.SetField, payload: { date: dateStr, isDirty: true } });
    } else {
      dispatchForm({ type: FormReducerActionType.SetField, payload: { date: null, isDirty: true } });
    }
  };

  const onTimeIsValidChange = (isSelected: boolean) => {
    dispatchForm({ type: FormReducerActionType.SetField, payload: { isTimeValid: !isSelected, isDirty: true } });
  }

  useDateFormatter({
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const handleUndoEnrichEvent = () => {
    formDataRef.current = null;
    const formDataToSet = preEnrichEvent.current ?? undefined;
    if (formDataToSet) {
      dispatchForm({ type: FormReducerActionType.SetForm, payload: { ...formDataToSet, isDirty: true } });
    }
    setEnriching(false);
    const restoredDescription = formDataToSet?.slideDescription || '';
    // Re-populate rawChatDescriptionRef if the restored pre-enrich snapshot contains
    // inline HTML (e.g. color spans from a chat response). Enrichment cleared the ref
    // when it ran; without this, resolveEffectiveDescription falls back to the stripped
    // markdownValue on save, dropping speaker color markup. Mirrors the same pattern
    // used when the modal first opens and when apply-text is called.
    rawChatDescriptionRef.current = /<[a-z][\s\S]*>/i.test(restoredDescription) ? restoredDescription : null;
    mdxInitialEmissionRef.current = null;
    descriptionUserHasEditedRef.current = false;
    setMarkdownValue(restoredDescription);
    if (enrichedData) {
      setEnrichedData(null);
    }
    setCanUndoEnrichEvent(false);
    setUndoEnrichEvent(false);
  }

  const getCurrentFormForEnrichment = (): FormData => {
    const originalEvent = currentEventIndex !== null ? events?.[currentEventIndex] : undefined;
    const merged = mergeLiveMediaIntoForm(formData, formDataRef.current);
    return {
      ...(originalEvent ?? {}),
      ...(merged ?? {}),
      // Use the effective description (original HTML when the user hasn't edited)
      // so enrich/undo-enrich snapshots don't drop speaker color markup that MDX
      // stripped but the user never intentionally changed. Matches save/navigation.
      slideDescription: resolveEffectiveDescription(),
    } as FormData;
  };

  const enrichEvent = async ({ useEventSourcesAsScope = false, keepCurrentMedia = true }: EnrichEventOptions = {}) => {
    if (currentEventIndex === null) {
      return;
    }
    const currentFormForEnrichment = getCurrentFormForEnrichment();
    setEnriching(true);
    preEnrichEvent.current = currentFormForEnrichment;
    setToast({
      setOpen: setToastOpen,
      open: true,
      location: 'bottom-right',
      time: Date.now(),
      duration: 60000,
      title: t('eventModal.enrichingTitle'),
      description: t('eventModal.enrichingBody'),
      level: ToastLevel.INFO,
    });
    let query = '';
    if (featureFlags?.enrichEventPrompt && (featureFlags?.enrichEventPrompt as string).trim() !== '') {
      const title = currentFormForEnrichment.slideTitle || undefined;
      const description = currentFormForEnrichment.slideDescription || '';
      query = featureFlags?.enrichEventPrompt as string;
      if (title && title.trim() !== '') {
        query = query.replace(/<titlePrompt>([\S\s]*?)<\/titlePrompt>/g, '$1');
        query = query.replace('{title}', title);
      } else {
        query = query.replace(/<titlePrompt>([\S\s]*?)<\/titlePrompt>/g, '');
      }
      if (description && description.trim() !== '') {
        query = query.replace(/<descriptionPrompt>([\S\s]*?)<\/descriptionPrompt>/g, '$1');
        query = query.replace('{description}', description);
      } else {
        query = query.replace(/<descriptionPrompt>([\S\s]*?)<\/descriptionPrompt>/g, '');
      }
    } else {
      query = 'Tell me more about';
      let useAnd = false;
      const titleForQuery = currentFormForEnrichment.slideTitle ?? '';
      if (enrichObject.enrichTitle && titleForQuery.trim() !== '') {
        query += ` "${titleForQuery}" as it relates to this case`;
        useAnd = true;
      }
      const descriptionForQuery = currentFormForEnrichment.slideDescription ?? '';
      if (enrichObject.enrichDescription && descriptionForQuery.trim() !== '') {
        query += `${useAnd ? ' and' : ''} "${descriptionForQuery}" as it relates to this case`;
        useAnd = true;
      }
    }
    try {
      const cellId = resolveCellIdFromIdToken(idToken || '');
      if (!cellId || !realAccessToken) return formData as Slide;
      const idTokenInfo = getTokenInfo(idToken || '');
      const accessTokenInfo = getTokenInfo(realAccessToken || '');
      const userId = getUserEmailForHeaders(idTokenInfo, accessTokenInfo);
      let body: any = {
        enrichEvent: true,
        query: query,
        selectedCase: caseId,
        ...caseFolderNameBody(caseFolderName),
        files: useEventSourcesAsScope ? getEventSourceUrlsForScope(currentFormForEnrichment.sources) : [],
        scopes: [],
        excludedFiles: [],
        excludedScopes: [],
        userId: userId,
        sessionId: null,
      };
      const caseApiName = getCaseInternalName(caseId).trim();
      if (caseApiName && caseApiName !== '*') {
        body.caseName = caseApiName;
      }
      const displayUser = String(getTokenInfo(idToken || '')?.name ?? '').trim();
      if (displayUser) {
        body.userName = displayUser;
      }
      const responseLanguage = responseLanguageForLocale(readStoredLocale());
      if (responseLanguage) body.responseLanguage = responseLanguage;
      let result = await postAssistantChat({
        cellId,
        accessToken: realAccessToken,
        idToken,
        body,
      });
      body = {
        enrichEvent: result?.enrich_event ?? false,
        sessionId: result?.sessionId,
        epochTimeSecs: result?.epoch_time_secs,
        chatKey: result?.chat_key,
        requestType: result?.request_type,
      }
      const startTime = Date.now();
      while (isEnrichmentHistoryPending(result?.status) && Date.now() - startTime < ENRICHMENT_POLL_TIMEOUT_MS) {
        await new Promise(resolve => setTimeout(resolve, ENRICHMENT_POLL_INTERVAL_MS));
        result = await fetchChatHistory({
          cellId,
          accessToken: realAccessToken,
          idToken,
          body,
        }) as Record<string, unknown>;
      }
      if (result?.status === 'error') {
        return formData as Slide;
      }
      const answer = JSON.parse(result?.answer as string);
      const enrichment = computeEnrichmentFromApiResponse(
        answer,
        jsonMediaDisplayEnabled,
      );
      if (enrichment) {
        const enrichedSources = enrichment.sources ?? [];
        const enrichedMedia = enrichment.media ?? [];
        const currentSources = currentFormForEnrichment.sources ?? [];
        const currentMedia = currentFormForEnrichment.media ?? [];
        const newSources = dedupeSources([...currentSources, ...enrichedSources]);
        const newMedia = mergeEnrichmentMedia(currentMedia, enrichedMedia, keepCurrentMedia);
        const currentSnapshot: EventFormSnapshot = {
          id: currentFormForEnrichment.id ?? null,
          displayOrder: currentFormForEnrichment.displayOrder ?? -1,
          date: currentFormForEnrichment.date ?? null,
          displayDate: currentFormForEnrichment.displayDate ?? currentFormForEnrichment.date ?? null,
          time: currentFormForEnrichment.time ?? null,
          isTimeValid: currentFormForEnrichment.isTimeValid === true,
          eventLocalTimezone: currentFormForEnrichment.eventLocalTimezone ?? null,
          slideTitle: currentFormForEnrichment.slideTitle || '',
          slideSubTitle: currentFormForEnrichment.slideSubTitle ?? undefined,
          slideDescription: currentFormForEnrichment.slideDescription || '',
          eventType: currentFormForEnrichment.eventType ?? '',
          sources: newSources,
          media: newMedia,
          show: currentFormForEnrichment.show ?? false,
          hideFromTimeline: currentFormForEnrichment.hideFromTimeline === true,
        };
        const newFormData = applyEnrichmentToForm(currentSnapshot, enrichment) as FormData;
        setCanUndoEnrichEvent(true);
        setEnrichedData(newFormData as Slide);
        rawChatDescriptionRef.current = null;
        mdxInitialEmissionRef.current = null;
        descriptionUserHasEditedRef.current = false;
        setMarkdownValue(newFormData.slideDescription || '');
        dispatchForm({ type: FormReducerActionType.SetForm, payload: { ...newFormData, isDirty: true } });
        setTimeout(() => {
          setToast({
            setOpen: setToastOpen,
            open: true,
            location: 'bottom-right',
            time: Date.now(),
            duration: 3000,
            title: t('eventModal.enrichedTitle'),
            description: t('eventModal.enrichedBody'),
            level: ToastLevel.SUCCESS,
          });
        }, 500);
      } else {
        throw new Error('Could not compute enrichment from API response');
      }
    } catch (error) {
      setTimeout(() => {
        CaptureError('Error enriching event: ' + error, error as Error);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 3000,
          title: t('eventModal.enrichFailedTitle'),
          description: t('eventModal.enrichFailedBody'),
          level: ToastLevel.ERROR,
        });
      }, 500);
    }
  }

  useEffect(() => {
    setLocalColumn(column);
  }, [column]);

  useEffect(() => {
    if (!newEvent && (currentEventIndexRef.current !== currentEventIndex || undoEnrichEvent || currentEventIndex === null)) {
      return;
    }
    // After a refetch re-sorts `events`, `currentEventIndex` is briefly stale: it
    // still points at the slot the opened event used to occupy. Re-initializing
    // from that slot would load a different slide and overwrite `currentEventIdRef`
    // with the wrong id, making the index sync chase the wrong event. If the
    // tracked event still exists but has moved, bail and let the sync effect
    // realign `currentEventIndex` first; this effect re-runs with the correct slot.
    if (!newEvent && currentEventIdRef.current != null && currentEventIndex !== null) {
      const trackedIndex = findEventIndexById(events, currentEventIdRef.current);
      if (trackedIndex !== -1 && trackedIndex !== currentEventIndex) {
        return;
      }
    }
    if (events && !deepEqual(events, eventsRef.current)) {
      eventsRef.current = events;
      formDataRef.current = null;
    }
    const initial = getInitialFormData({
      localColumn,
      eventsFromChat,
      events,
      currentEventIndex,
      mediaSourcesFromChat,
      sourcesFromChat,
      enriching,
      currentFormRefValue: formDataRef.current,
      preEnrichEventValue: preEnrichEvent.current,
      jsonMediaDisplayEnabled,
    });
    if (initial !== null && !formData?.isDirty) {
      if (deepEqual(initial, formDataRef.current)) return;
      formDataRef.current = initial;
      formDataInitial.current = initial;
      // Record the stable id so refetch/re-sort can re-resolve the position.
      currentEventIdRef.current = initial.id ?? null;
      dispatchForm({ type: FormReducerActionType.SetForm, payload: initial });
      const initialDescription = initial.slideDescription || '';
      // If the description contains inline HTML (e.g. color spans from AI responses),
      // preserve the full HTML in a ref so it survives MDXEditor's HTML-stripping on mount.
      rawChatDescriptionRef.current = /<[a-z][\s\S]*>/i.test(initialDescription)
        ? initialDescription
        : null;
      mdxInitialEmissionRef.current = null;
      descriptionUserHasEditedRef.current = false;
      setMarkdownValue(initialDescription);
      if (enrichedData) setEnrichedData(null);
      setEnrichObject({
        enrichTitle: !!(initial.slideTitle?.trim()),
        enrichDescription: !!(initial.slideDescription?.trim()),
        enrichSources: !!(initial.sources?.length),
        enrichMedia: !!(initial.media?.length),
      });
    } else if (enrichedData) {
      dispatchForm({ type: FormReducerActionType.SetForm, payload: { ...enrichedData as FormData, isDirty: true } });
      setEnrichedData(null);
    }
  }, [
    events,
    currentEventIndex,
    localColumn,
    eventsFromChat,
    enrichedData,
    undoEnrichEvent,
    enriching,
    mediaSourcesFromChat,
    sourcesFromChat,
    newEvent,
    jsonMediaDisplayEnabled,
  ]);

  // Moves to the adjacent event relative to the event currently being edited
  // (resolved by stable id, falling back to the held index). The target index is
  // bounds-checked: stepping past either end closes the modal instead of leaving
  // the index pointing at an undefined slide. The tracked id is advanced to the
  // neighbour so a subsequent refetch/re-sort re-resolves to the right slide.
  const navigateToAdjacent = (dir: 'left' | 'right') => {
    const slides = events ?? [];
    const resolvedIndex = findEventIndexById(slides, currentEventIdRef.current);
    const baseIndex = resolvedIndex !== -1 ? resolvedIndex : (currentEventIndex ?? 0);
    const targetIndex = dir === 'right' ? baseIndex + 1 : baseIndex - 1;

    formDataRef.current = null;
    formDataInitial.current = null;
    dispatchForm({ type: FormReducerActionType.SetForm, payload: undefined });

    if (targetIndex < 0 || targetIndex > slides.length - 1) {
      currentEventIdRef.current = null;
      onClose();
      return;
    }

    currentEventIdRef.current = slides[targetIndex]?.id ?? null;
    setCurrentEventIndex(targetIndex);
  };

  // Resolves the description used for saving and unsaved-changes checks. When the
  // event loaded HTML-rich content (e.g. color spans) that MDXEditor rewrote to
  // stripped markdown, an untouched field must still be treated as the original
  // HTML so navigation/cancel don't falsely flag unsaved changes and save keeps
  // the colors. descriptionUserHasEditedRef is set on the second debounced emission
  // so we don't rely on comparing markdownValue (which can be stale during the
  // 300ms debounce window and cause a false "no edit" result).
  const resolveEffectiveDescription = () => {
    if (!rawChatDescriptionRef.current) return markdownValue;
    if (descriptionUserHasEditedRef.current) return markdownValue;
    return rawChatDescriptionRef.current;
  };

  const hasEditableUnsavedChanges = useMemo(() => {
    if (!formDataInitial.current || !formData) return false;
    const formDataToCheck = formSnapshotForUnsavedCheck(
      formData,
      formDataRef.current,
      resolveEffectiveDescription(),
    );
    return hasUnsavedChanges(formDataToCheck, formDataInitial.current);
  }, [formData, markdownValue]);

  const saveChangesEnabled =
    (newEvent || hasEditableUnsavedChanges) && saveStatus !== 'saving';
  const closeWithoutChanges = !newEvent && !hasEditableUnsavedChanges;
  const saveUnsavedChangesLabel =
    navDir === 'right'
      ? t('eventModal.saveAndGoNext')
      : navDir === 'left'
        ? t('eventModal.saveAndGoPrevious')
        : t('eventModal.saveAndClose');

  const preserveCardBodyScroll = useCallback(() => {
    if (cardBodyScrollRef.current) {
      cardBodyScrollTopRef.current = cardBodyScrollRef.current.scrollTop;
    }
  }, []);

  const restoreCardBodyScroll = useCallback(() => {
    requestAnimationFrame(() => {
      if (cardBodyScrollRef.current) {
        cardBodyScrollRef.current.scrollTop = cardBodyScrollTopRef.current;
      }
    });
  }, []);

  const clearSaveStatusDismissListeners = useCallback(() => {
    saveStatusDismissCleanupRef.current?.();
    saveStatusDismissCleanupRef.current = null;
  }, []);

  const scheduleSaveStatusDismiss = useCallback(() => {
    clearSaveStatusDismissListeners();
    saveStatusShownAtRef.current = Date.now();
    const maxTimer = window.setTimeout(() => {
      setSaveStatus('idle');
      clearSaveStatusDismissListeners();
    }, 3000);
    const dismissIfReady = () => {
      if (Date.now() - saveStatusShownAtRef.current >= 1000) {
        setSaveStatus('idle');
        clearSaveStatusDismissListeners();
      }
    };
    const onPointerDown = () => dismissIfReady();
    const onKeyDown = () => dismissIfReady();
    const onMouseMove = () => dismissIfReady();
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousemove', onMouseMove);
    saveStatusDismissCleanupRef.current = () => {
      window.clearTimeout(maxTimer);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [clearSaveStatusDismissListeners]);

  useEffect(() => () => clearSaveStatusDismissListeners(), [clearSaveStatusDismissListeners]);

  useEffect(() => {
    if (!isOpen) {
      setSaveStatus('idle');
      clearSaveStatusDismissListeners();
      suppressFormReinitOnceRef.current = false;
    }
  }, [isOpen, clearSaveStatusDismissListeners]);

  useEffect(() => {
    setSaveStatus('idle');
    clearSaveStatusDismissListeners();
  }, [currentEventIndex, newEvent, clearSaveStatusDismissListeners]);

  const applyServerEventPatches = useCallback(
    (serverEvent: Slide, serverRecord?: Record<string, unknown>) => {
      const current = formDataRef.current;
      if (!current) return;
      const patch = serverRecord
        ? buildPartialServerFormPatch(serverRecord, serverEvent)
        : slideToFormSyncPatch(serverEvent);
      const delta: Partial<FormData> = {};
      for (const key of FORM_SERVER_SYNC_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(patch, key)) continue;
        const nextValue = patch[key];
        const currentValue = current[key];
        if (!deepEqual(currentValue, nextValue)) {
          (delta as Record<string, unknown>)[key] = nextValue;
        }
      }
      if (Object.keys(delta).length === 0) return;
      preserveCardBodyScroll();
      const nextForm = { ...current, ...delta, isDirty: false } as FormData;
      formDataRef.current = nextForm;
      formDataInitial.current = nextForm;
      dispatchForm({
        type: FormReducerActionType.SetField,
        payload: { ...delta, isDirty: false },
      });
      if (Object.prototype.hasOwnProperty.call(delta, 'slideDescription')) {
        const nextDescription = delta.slideDescription ?? '';
        setMarkdownValue(nextDescription);
      }
      if (Object.prototype.hasOwnProperty.call(delta, 'courtroomDescription')) {
        setCourtroomMarkdownValue(delta.courtroomDescription ?? '');
      }
      restoreCardBodyScroll();
    },
    [preserveCardBodyScroll, restoreCardBodyScroll],
  );

  useEffect(() => {
    if (!newEvent && (currentEventIndexRef.current !== currentEventIndex || undoEnrichEvent || currentEventIndex === null)) {
      return;
    }
    // After a refetch re-sorts `events`, `currentEventIndex` is briefly stale: it
    // still points at the slot the opened event used to occupy. Re-initializing
    // from that slot would load a different slide and overwrite `currentEventIdRef`
    // with the wrong id, making the index sync chase the wrong event. If the
    // tracked event still exists but has moved, bail and let the sync effect
    // realign `currentEventIndex` first; this effect re-runs with the correct slot.
    if (!newEvent && currentEventIdRef.current != null && currentEventIndex !== null) {
      const trackedIndex = findEventIndexById(events, currentEventIdRef.current);
      if (trackedIndex !== -1 && trackedIndex !== currentEventIndex) {
        return;
      }
    }
    if (suppressFormReinitOnceRef.current) {
      suppressFormReinitOnceRef.current = false;
      if (events) {
        eventsRef.current = events;
      }
      return;
    }
    if (events && !deepEqual(events, eventsRef.current)) {
      eventsRef.current = events;
      formDataRef.current = null;
    }
    const initial = getInitialFormData({
      localColumn,
      eventsFromChat,
      events,
      currentEventIndex,
      mediaSourcesFromChat,
      sourcesFromChat,
      enriching,
      currentFormRefValue: formDataRef.current,
      preEnrichEventValue: preEnrichEvent.current,
      jsonMediaDisplayEnabled,
    });
    if (initial !== null && !formData?.isDirty) {
      if (deepEqual(initial, formDataRef.current)) return;
      formDataRef.current = initial;
      formDataInitial.current = initial;
      // Record the stable id so refetch/re-sort can re-resolve the position.
      currentEventIdRef.current = initial.id ?? null;
      dispatchForm({ type: FormReducerActionType.SetForm, payload: initial });
      const initialDescription = ensureCitationSupInlineStyles(
        initial.slideDescription || '',
      );
      // If the description contains inline HTML (e.g. color spans from AI responses),
      // preserve the full HTML in a ref so it survives MDXEditor's HTML-stripping on mount.
      rawChatDescriptionRef.current = /<[a-z][\s\S]*>/i.test(initialDescription)
        ? initialDescription
        : null;
      mdxInitialEmissionRef.current = null;
      descriptionUserHasEditedRef.current = false;
      setMarkdownValue(initialDescription);
      setCourtroomMarkdownValue(initial.courtroomDescription || '');
      if (enrichedData) setEnrichedData(null);
      setEnrichObject({
        enrichTitle: !!(initial.slideTitle?.trim()),
        enrichDescription: !!(initial.slideDescription?.trim()),
        enrichSources: !!(initial.sources?.length),
        enrichMedia: !!(initial.media?.length),
      });
    } else if (enrichedData) {
      dispatchForm({ type: FormReducerActionType.SetForm, payload: { ...enrichedData as FormData, isDirty: true } });
      setEnrichedData(null);
    }
  }, [
    events,
    currentEventIndex,
    localColumn,
    eventsFromChat,
    enrichedData,
    undoEnrichEvent,
    enriching,
    mediaSourcesFromChat,
    sourcesFromChat,
    newEvent,
    jsonMediaDisplayEnabled,
    applyServerEventPatches,
    formData?.isDirty,
  ]);

  const scrollLeft = () => {
    if (currentEventIndex === null) {
      return;
    }
    const formDataToCheck = formSnapshotForUnsavedCheck(
      formData,
      formDataRef.current,
      resolveEffectiveDescription(),
    );
    if (hasUnsavedChanges(formDataToCheck, formDataInitial.current)) {
      setNavDir('left');
      setShowAlertDialog(true);
    } else {
      navigateToAdjacent('left');
    }
  };

  const scrollRight = () => {
    if (currentEventIndex === null) {
      return;
    }
    const formDataToCheck = formSnapshotForUnsavedCheck(
      formData,
      formDataRef.current,
      resolveEffectiveDescription(),
    );
    if (hasUnsavedChanges(formDataToCheck, formDataInitial.current)) {
      setNavDir('right');
      setShowAlertDialog(true);
    } else {
      navigateToAdjacent('right');
    }
  };

  const handleMarkdownChange = (newMarkdown: string) => {
    if (!editingEnabled || isLegacy) return;
    if (!newMarkdown || newMarkdown.trim() === '') {
      setEnrichObject({ ...enrichObject, enrichDescription: false });
    } else {
      setEnrichObject({ ...enrichObject, enrichDescription: true });
    }
    // Capture MDXEditor's first emission so we can swallow it; track whether
    // the user has edited by comparing against that baseline on every change.
    // Using assignment (not a one-way set) means an edit-then-undo that restores
    // the original text clears the flag, so resolveEffectiveDescription will
    // return rawChatDescriptionRef again instead of stripped markdownValue.
    if (rawChatDescriptionRef.current !== null) {
      if (mdxInitialEmissionRef.current === null) {
        mdxInitialEmissionRef.current = newMarkdown;
      } else {
        descriptionUserHasEditedRef.current = newMarkdown !== mdxInitialEmissionRef.current;
      }
    }
    setMarkdownValue(newMarkdown);
  }

  const handleMarkdownChangeFinal = () => {
    if (!editingEnabled || isLegacy) return;
    if (!markdownValue || markdownValue.trim() === '') {
      setEnrichObject({ ...enrichObject, enrichDescription: false });
    } else {
      setEnrichObject({ ...enrichObject, enrichDescription: true });
    }
    dispatchForm({ type: FormReducerActionType.SetField, payload: { slideDescription: markdownValue, isDirty: true } });
  }

  const handleCourtroomMarkdownChange = (newMarkdown: string) => {
    if (!editingEnabled) return;
    setCourtroomMarkdownValue(newMarkdown);
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: { courtroomDescription: newMarkdown, isDirty: true },
    });
  };

  const applyTextToEventDescriptionImplRef = useRef<(text: string) => void>(
    () => {},
  );

  applyTextToEventDescriptionImplRef.current = (text: string) => {
    if (!editingEnabled || isLegacy) return;
    const t = text ?? "";
    if (!t.trim()) {
      setEnrichObject((prev) => ({ ...prev, enrichDescription: false }));
    } else {
      setEnrichObject((prev) => ({ ...prev, enrichDescription: true }));
    }
    // If the applied text contains inline HTML (e.g. color spans from a chat
    // response), preserve it so resolveEffectiveDescription can return the
    // full HTML on save — the same pattern used when the modal first opens.
    rawChatDescriptionRef.current = /<[a-z][\s\S]*>/i.test(t) ? t : null;
    mdxInitialEmissionRef.current = null;
    descriptionUserHasEditedRef.current = false;
    setMarkdownValue(t);
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: { slideDescription: t, isDirty: true },
    });
  };

  useEffect(() => {
    if (!registerApplyTextToEventDescription) return;
    if (!isOpen || !editingEnabled || isLegacy) {
      registerApplyTextToEventDescription(null);
      return;
    }
    registerApplyTextToEventDescription((txt) =>
      applyTextToEventDescriptionImplRef.current(txt),
    );
    return () => registerApplyTextToEventDescription(null);
  }, [isOpen, editingEnabled, isLegacy, registerApplyTextToEventDescription]);

  const handleTimePresses = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!editingEnabled || isLegacy) return;
    const hourMinuteOrSecond = (e.target as any)?.dataset?.type;
    if (hourMinuteOrSecond !== 'second') {
      let timeStr = (timeInputRef.current?.innerText || '').replace(/\p{Pd}/gu, '_');
      timeStr = timeStr.replace(/[^0-9:AMP_]*/g, '').replace('PM', ' PM').replace('AM', ' AM');
      const { hoursNumber, minutesNumber, secondsNumber } = getTimeFromStr(timeStr);
      if (!isNaN(hoursNumber as any) && !isNaN(minutesNumber as any) && isNaN(secondsNumber as any)) {
        const newTime = new Time(hoursNumber, minutesNumber, 0);
        handleTimeChange(newTime);
      }
    }
  }

  const handleTimeChange = (time: Time | null) => {
    if (!editingEnabled || isLegacy) return;
    const date = formData?.date ? getDateFromDateStr(formData.date) : null;
    dispatchForm({
      type: FormReducerActionType.SetField,
      payload: {
        date: date ? date.toString() : null,
        time: time ? `${time.hour}:${time.minute}:${time.second}` : null,
        isDirty: true,
      },
    });
  };

  const handleVisibilityClick = async () => {
    if (!events) return;
    dispatchForm({ type: FormReducerActionType.SetField, payload: { show: !formData?.show, isDirty: true } });
  };

  const handleSubmit = async (
    e: React.FormEvent,
    { closeAfterSave = false }: { closeAfterSave?: boolean } = {},
  ) => {
    if (!editingEnabled || isLegacy || (currentEventIndex === null && !newEvent)) {
      return;
    }
    const liveUnsavedChanges = hasUnsavedChanges(
      formSnapshotForUnsavedCheck(
        formData,
        formDataRef.current,
        resolveEffectiveDescription(),
      ),
      formDataInitial.current,
    );
    if (!newEvent && !liveUnsavedChanges) {
      return;
    }
    if (saveStatus === 'saving') {
      return;
    }
    if (e.preventDefault) {
      e.preventDefault();
    }
    const shouldNavigate = navDir === 'right' || navDir === 'left';
    const descriptionToSave = resolveEffectiveDescription();
    const formDataToSet = normalizeTimelineEventStillFrames({
      ...formData,
      sources: formDataRef.current?.sources ?? formData?.sources,
      media: formDataRef.current?.media ?? formData?.media,
      slideTitle: formData?.slideTitle,
      slideSubTitle: formData?.slideSubTitle,
      slideDescription: descriptionToSave,
      courtroomTitle: formData?.courtroomTitle,
      courtroomDescription: courtroomMarkdownValue,
    });
    if (formDataToSet && !formDataToSet?.time) {
      formDataToSet.isTimeValid = false;
      formDataToSet.time = undefined;
      formDataToSet.eventLocalTimezone = undefined;
      formDataToSet.slideDescription = descriptionToSave;
    }
    const { isDirty: _omit, ...eventPayload } = formDataToSet;

    clearSaveStatusDismissListeners();
    setSaveStatus('saving');
    preserveCardBodyScroll();

    try {
      if (newEvent) {
        const result = await onAddEvent(eventPayload as unknown as Slide);
        if (result.success) {
          setSaveStatus('success');
          scheduleSaveStatusDismiss();
          setShowAlertDialog(false);
          setNavDir('no');
          window.setTimeout(() => onClose(), 1000);
        } else {
          restoreCardBodyScroll();
          setSaveStatus('error');
          scheduleSaveStatusDismiss();
        }
        return;
      }

      const result = await onEditEvent(eventPayload as unknown as Slide);
      if (result.success) {
        const reconciled = { ...formDataToSet, isDirty: false } as FormData;
        formDataRef.current = reconciled;
        formDataInitial.current = reconciled;
        dispatchForm({
          type: FormReducerActionType.SetField,
          payload: { isDirty: false },
        });
        if (result.event && !shouldNavigate) {
          applyServerEventPatches(result.event, result.serverRecord);
        }
        if (!shouldNavigate) {
          suppressFormReinitOnceRef.current = true;
        }
        restoreCardBodyScroll();
        setSaveStatus('success');
        scheduleSaveStatusDismiss();
        setShowAlertDialog(false);
        if (shouldNavigate) {
          navigateToAdjacent(navDir);
        } else if (closeAfterSave) {
          // Matches the add-event flow: leave the success banner up briefly so
          // the save is acknowledged before the modal disappears.
          window.setTimeout(() => onClose(), 1000);
        }
        setNavDir('no');
      } else {
        suppressFormReinitOnceRef.current = false;
        restoreCardBodyScroll();
        setSaveStatus('error');
        scheduleSaveStatusDismiss();
        setShowAlertDialog(false);
        setNavDir('no');
      }
    } catch {
      suppressFormReinitOnceRef.current = false;
      restoreCardBodyScroll();
      setSaveStatus('error');
      scheduleSaveStatusDismiss();
      setShowAlertDialog(false);
      setNavDir('no');
    }
  };

  const handleUnifiedSourcesChanges = useCallback(
    (
      sources: MediaSource[],
      media: MediaSource[],
      options?: { markDirty?: boolean },
    ) => {
      if (!editingEnabled || isLegacy) return;
      const syncedSources = syncPairedSourceContextsFromMedia(sources, media);
      const sanitizedSources = syncedSources.map((source) => stripStillFrameThumbnailFromSource(source as unknown as Record<string, unknown>) as MediaSource);
      const markDirty = options?.markDirty !== false;
      const nextFields = {
        sources: sanitizedSources,
        media,
      } as Pick<FormData, 'sources' | 'media'>;
      const previous = formDataRef.current;
      const alreadyDirty = formIsDirtyAgainstBaseline(
        previous ?? undefined,
        previous,
        formDataInitial.current,
      );
      formDataRef.current = {
        ...(previous ?? {}),
        ...nextFields,
      } as FormData;
      // Hydration writes (markDirty: false) advance the unsaved-changes baseline
      // only while the form is still clean. If the user already edited (including
      // during an in-flight thumbnail backfill), replacing the baseline with live
      // sources/media would make those edits look clean (TR-2983).
      if (!markDirty) {
        formDataInitial.current = nextHydrationBaseline(
          formDataInitial.current,
          nextFields,
          alreadyDirty,
        );
      }
      dispatchForm({
        type: FormReducerActionType.SetField,
        payload: {
          ...nextFields,
          ...(markDirty || alreadyDirty ? { isDirty: true } : {}),
        },
      });
      setEnrichObject((prev) => ({
        ...prev,
        enrichSources: sanitizedSources.length > 0,
        enrichMedia: media.length > 0,
      }));
    },
    [editingEnabled, isLegacy],
  );

  const handleCancel = () => {
    const formDataToCheck = formSnapshotForUnsavedCheck(
      formData,
      formDataRef.current,
      resolveEffectiveDescription(),
    );
    const unsavedChanges = hasUnsavedChanges(formDataToCheck, formDataInitial.current);
    if (unsavedChanges) {
      setNavDir('no');
      setShowAlertDialog(true);
    } else {
      dispatchForm({ type: FormReducerActionType.SetForm, payload: undefined });
      onClose();
    }
  }

  const handleAlertDialogClose = () => {
    setShowAlertDialog(false);
    if (currentEventIndex === null) {
      return;
    }
    if (navDir === 'right') {
      navigateToAdjacent('right');
    } else if (navDir === 'left') {
      navigateToAdjacent('left');
    } else {
      dispatchForm({ type: FormReducerActionType.SetForm, payload: formDataRef.current ?? undefined });
      onClose();
      formDataRef.current = null;
      formDataInitial.current = null;
    }
  }

  const handleAlertDialogConfirm = () => {
    // Keep the dialog up until save actually starts/succeeds. Dismissing first
    // then early-returning from handleSubmit left the Edit Event page open with
    // no feedback (TR-2844).
    const closeAfterSave = navDir !== 'right' && navDir !== 'left';
    void handleSubmit({ preventDefault() {} } as React.FormEvent, { closeAfterSave });
  }

  /*
   * The Modal header is a single-line 36px bar, so it carries only the short
   * dialog name. The event's own (long, wrapping) title is not repeated here —
   * it is already the "Title" field at the top of the body.
   */
  const eventModalTitle =
    editingEnabled && !isLegacy
      ? !!newEvent
        ? t('eventModal.addNew')
        : currentEventIndex !== -1
          ? t('eventModal.eventPosition', { number: (currentEventIndex || 0) + 1 })
          : t('eventModal.event')
      : events?.length
        ? t('eventModal.details', {
            order: events?.[currentEventIndex || 0]?.displayOrder ?? '',
            total: events.length,
          })
        : t('eventModal.detailsNoTotal', {
            order: events?.[currentEventIndex || 0]?.displayOrder ?? '',
          });

  const eventModalFooter = (
    <div className="relative flex w-full flex-row items-center justify-between gap-4">
      {(!editingEnabled || isLegacy) && (
        <Button
          size="md"
          style="solid"
          color="subdued"
          background="default"
          aria-label={t('timeline.dialog.cancel')}
          onClick={handleCancel}
        >
          {t('eventModal.close')}
        </Button>
      )}
      {editingEnabled && !isLegacy && (
        <>
          <Button
            size="md"
            style="text"
            color="light"
            background="inverse"
            aria-label={t('timeline.dialog.cancel')}
            onClick={handleCancel}
          >
            {t('timeline.dialog.cancel')}
          </Button>
          <div className="flex flex-row items-center gap-2">
            {!newEvent && (
              <Button
                size="md"
                style="solid"
                color="accent"
                background="inverse"
                aria-label={t('eventModal.saveKeepEditing')}
                data-testid="event-modal-save"
                disabled={!saveChangesEnabled}
                onClick={(e) => handleSubmit(e, { closeAfterSave: false })}
              >
                {t('eventModal.save')}
              </Button>
            )}
            <Button
              size="md"
              style="solid"
              color="accent"
              background="inverse"
              aria-label={newEvent ? t('eventModal.addEventAria') : t('eventModal.saveAndCloseAria')}
              data-testid="event-modal-save-and-close"
              type="button"
              disabled={!saveChangesEnabled}
              onClick={(e) => handleSubmit(e, { closeAfterSave: true })}
            >
              {!!newEvent ? t('timeline.table.addEvent') : t('eventModal.saveAndClose')}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <Modal
        isOpen={showAlertDialog}
        onClose={() => setShowAlertDialog(false)}
        onOpenChange={(open) => {
          if (!open) setShowAlertDialog(false);
        }}
        size="sm"
        title={t('eventModal.unsavedTitle')}
        className="z-[100001]"
        dismissOnEscape={true}
        dismissOnBackdropClick={true}
        footer={
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <Button
              type="button"
              size="md"
              style="text"
              color="light"
              background="inverse"
              aria-label={t('eventModal.discard')}
              onClick={handleAlertDialogClose}
            >
              {t('eventModal.discard')}
            </Button>
            <Button
              type="button"
              size="md"
              style="solid"
              color="accent"
              background="inverse"
              aria-label={saveUnsavedChangesLabel}
              onClick={handleAlertDialogConfirm}
            >
              {saveUnsavedChangesLabel}
            </Button>
          </div>
        }
      >
        <p className="m-0 text-sm text-[var(--text-inverse,#ffffff)]">
          {t('eventModal.unsavedBody')}
        </p>
      </Modal>
      {!undoEnrichEvent && ((!!events && currentEventIndex !== null && !!events?.[currentEventIndex]) || !!newEvent) ? (
        <Modal
          isOpen
          // Escape stays with `HotKeys` below (it is the only handler that also
          // fires from inside inputs and the markdown editor), and both routes
          // go through `handleCancel` so the unsaved-changes prompt can't be
          // bypassed. Backdrop dismissal is off for the same reason — a stray
          // click must not discard an in-progress edit.
          dismissOnEscape={false}
          dismissOnBackdropClick={false}
          onClose={handleCancel}
          size="xl"
          id={`event-modal-${(currentEventIndex || 0) + 1}`}
          title={eventModalTitle}
          // Serenity Patterns sizes the Edit Event frame at 795px; `xl` (960px)
          // is the closest theme size, so cap it here rather than patching the
          // Modal's own size scale.
          contentClassName="h-[calc(100vh-120px)] sm:max-w-[795px]"
          bodyClassName="custom-scrollbar2 flex items-start justify-center px-2 sm:px-4 min-w-0 overflow-x-hidden overflow-y-auto"
          // Paging sits beside the title (the Figma `.PaneTitle` group), not with
          // the trailing controls. The Button's own `:disabled` opacity conveys
          // the ends of the range, so no opacity classes are needed here.
          titleActions={
            !NEW_EVENT_COLUMNS.includes(column || '') ? (
              <>
                <Button
                  {...headerControlButtonProps}
                  iconOnly
                  aria-label={t('eventModal.previous')}
                  className="hidden sm:inline-flex"
                  disabled={(currentEventIndex || 0) < 1}
                  onClick={scrollLeft}
                >
                  <ArrowLeftIcon size={HEADER_ICON_SIZE} />
                </Button>
                <Button
                  {...headerControlButtonProps}
                  iconOnly
                  aria-label={t('eventModal.next')}
                  className="hidden sm:inline-flex"
                  disabled={(currentEventIndex || 0) > (events?.length || 0) - 2}
                  onClick={scrollRight}
                >
                  <ArrowRightIcon size={HEADER_ICON_SIZE} />
                </Button>
              </>
            ) : null
          }
          headerActions={
            <div className="flex flex-row items-center gap-1">
              <HotKeys
                onCloseModal={handleCancel}
                disableCloseModal={showAlertDialog}
                isModal={true}
              />
              {(toolsDrawer || localColumn !== 'timelineTitle') && (
              <div className="flex flex-row gap-1 justify-end items-center shrink-0">
                {localColumn !== 'timelineTitle' && (
                  <>
                  {canUndoEnrichEvent && (
                    <Button
                      {...headerControlButtonProps}
                      aria-label={t('eventModal.undo')}
                      className="hidden sm:inline-flex"
                      disabled={!canUndoEnrichEvent}
                      onClick={handleUndoEnrichEvent}
                      leadingIcon={<EraserIcon size={HEADER_ICON_SIZE} />}
                    >
                      {t('eventModal.undo')}
                    </Button>
                  )}
                  <Popover
                    isOpen={enrichPopoverVisible}
                    onOpenChange={setEnrichPopoverVisible}
                    placement="bottom-end"
                    variant="inverted"
                    maxWidth="300px"
                    showArrow
                    aria-label={t('eventModal.enrichOptions')}
                    content={
                      <div className="flex flex-col gap-3 w-[300px] max-w-full">
                        <p className="m-0 font-light text-sm text-wrap tracking-wide text-[var(--tp-popover-fg)]">
                          {t('eventModal.enrichHint')}
                        </p>
                        <Checkbox
                          checked={enrichWithScope}
                          onCheckedChange={handleEnrichWithScopeChange}
                          label={t('eventModal.useSelectedSources')}
                          className="text-[var(--tp-popover-fg)]"
                        />
                        <Checkbox
                          checked={keepCurrentMediaOnEnrich}
                          onCheckedChange={handleKeepCurrentMediaOnEnrichChange}
                          label={t('timeline.dialog.keepCurrentMedia')}
                          className="text-[var(--tp-popover-fg)]"
                        />
                        <div className="flex flex-row gap-2 items-center w-full">
                          <Button
                            size="sm"
                            style="solid"
                            color="accent"
                            aria-label={t('eventModal.enrich')}
                            type="button"
                            className="flex-1"
                            disabled={Object.values(enrichObject).every(value => value === false)}
                            onClick={() => {
                              enrichEvent({
                                useEventSourcesAsScope: enrichWithScope,
                                keepCurrentMedia: keepCurrentMediaOnEnrich,
                              });
                              setEnrichPopoverVisible(false);
                            }}
                            leadingIcon={<MagicWandIcon weight="duotone" />}
                          >
                            {t('eventModal.enrich')}
                          </Button>
                          <Button
                            size="sm"
                            style="text"
                            aria-label={t('eventModal.cancelEnrich')}
                            type="button"
                            className="flex-1"
                            onClick={() => {
                              setEnrichPopoverVisible(false);
                            }}
                            leadingIcon={<XIcon />}
                          >
                            {t('timeline.dialog.cancel')}
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    <Button
                      {...headerControlButtonProps}
                      aria-label={t('eventModal.enrich')}
                      aria-haspopup="dialog"
                      className="hidden sm:inline-flex"
                      leadingIcon={<MagicWandIcon size={HEADER_ICON_SIZE} />}
                      onClick={() => setEnrichPopoverVisible((visible) => !visible)}
                    >
                      <span className="text-nowrap">{t('eventModal.enrich')}</span>
                    </Button>
                  </Popover>
                  </>
                )}
                {toolsDrawer && (
                  <TpTooltip
                    content={t('eventModal.favorites')}
                    placement={'bottom'}
                    delayDuration={0}
                    sideOffset={5}
                  >
                    <Button
                      {...headerControlButtonProps}
                      iconOnly
                      aria-label={t('eventModal.openTools')}
                      title={t('eventModal.toolsTitle')}
                      onClick={() => toolsDrawer.openDrawerToTab('sources')}
                    >
                      <BookmarksSimpleIcon size={HEADER_ICON_SIZE} />
                    </Button>
                  </TpTooltip>
                )}
              </div>
              )}
            </div>
          }
          footer={eventModalFooter}
        >
            <div className="relative w-full">
            {(showLoadingEventDetailsHeader || saveStatus === 'saving' || saveStatus === 'success' || saveStatus === 'error') && (
              <div
                className="absolute inset-0 z-30 flex items-center justify-center rounded-lg bg-gray-700/95 px-4"
                role="status"
                aria-live="polite"
              >
                {saveStatus === 'saving' && (
                  <div className="flex items-center gap-2 text-white font-light tracking-wide">
                    <Spinner size="sm" color="white" variant="wave" />
                    <span>{t('eventModal.saving')}</span>
                  </div>
                )}
                {saveStatus === 'success' && (
                  <div className="flex items-center gap-2 text-green-400 font-light tracking-wide">
                    <CheckCircleIcon weight="duotone" size={24} />
                    <span>{t('eventModal.saved')}</span>
                  </div>
                )}
                {saveStatus === 'error' && (
                  <div className="flex items-center gap-2 text-red-400 font-light tracking-wide">
                    <WarningCircleIcon weight="duotone" size={24} />
                    <span>{t('eventModal.saveError')}</span>
                  </div>
                )}
                {saveStatus === 'idle' && showLoadingEventDetailsHeader && (
                  <div className="flex items-center gap-2 text-white font-light tracking-wide">
                    <Spinner size="sm" color="white" variant="wave" />
                    <span>{t('eventModal.loadingDetails')}</span>
                  </div>
                )}
              </div>
            )}
            <div
              ref={cardBodyScrollRef}
              onSubmit={handleSubmit}
              id={`event-modal-description-${(currentEventIndex || 0) + 1}`}
              className="flex flex-col gap-8 items-center text-white font-inter bg-transparent w-full max-w-full pt-3 pb-3"
            >
              <div className="flex flex-col gap-8 items-start w-full">
                <div className="flex flex-col gap-2 items-start w-full">
                  <div className="flex w-full flex-row flex-wrap items-end justify-between gap-2">
                    <div className="flex flex-col sm:flex-row gap-2 items-start">
                      <div className="flex flex-row gap-2 items-end">
                        <DatePicker
                          aria-label={t('eventModal.date')}
                          value={formData?.date ? getDateFromDateStr(formData.date) : null}
                          onChange={handleDateChange}
                          label={t('eventModal.date')}
                          labelPlacement="outside"
                          size="md"
                          radius="md"
                          variant="bordered"
                          classNames={{
                            base: 'inverse-form-surface w-fit',
                            label: comboboxAlignedFieldClassNames.label,
                            inputWrapper: comboboxAlignedFieldClassNames.inputWrapper,
                            innerWrapper: comboboxAlignedFieldClassNames.innerWrapper,
                            input: comboboxAlignedFieldClassNames.input,
                            selectorButton: 'text-[var(--color-text-placeholder)]',
                            selectorIcon: 'text-[var(--color-text-placeholder)]',
                            popoverContent: 'text-gray-800 rounded-md shadow-lg',
                            calendar: 'rounded-md text-black border-2 border-solid border-white/10',
                            calendarContent: 'text-gray-800',
                          }}
                        />
                        <Button
                          {...clearFieldButtonProps}
                          aria-label={t('eventModal.clearDate')}
                          onClick={() => handleDateChange(null)}
                        >
                          <XCircleIcon weight="duotone" size={CLEAR_FIELD_ICON_SIZE} />
                        </Button>
                      </div>
                      {formData?.date && (
                        <>
                          <div className="flex flex-row items-end gap-2">
                            <TimeInput
                              ref={timeInputRef}
                              isDisabled={!formData?.date}
                              value={formData?.time ? getClientTimeFromStr(formData.time) : null}
                              onChange={handleTimeChange}
                              radius="md"
                              classNames={{
                                base: 'inverse-form-surface w-fit',
                                label: comboboxAlignedFieldClassNames.label,
                                inputWrapper: comboboxAlignedFieldClassNames.inputWrapper,
                                innerWrapper: comboboxAlignedFieldClassNames.innerWrapper,
                                input: comboboxAlignedFieldClassNames.input,
                                segment: comboboxAlignedFieldClassNames.segment,
                              }}
                              variant="bordered"
                              labelPlacement="outside"
                              label={t('eventModal.time')}
                              size="md"
                              granularity="second"
                              onKeyUp={handleTimePresses}
                            />
                            <Button
                              {...clearFieldButtonProps}
                              aria-label={t('eventModal.clearTime')}
                              onClick={() => handleTimeChange(null)}
                            >
                              <XCircleIcon weight="duotone" size={CLEAR_FIELD_ICON_SIZE} />
                            </Button>
                          </div>
                          <div className="box-border flex flex-col justify-start items-center gap-1">
                            <div className="text-white/100 w-full font-light text-left text-[14px] mt-[2px] p-0 whitespace-nowrap">{t('eventModal.timezone')}</div>
                            <div className="box-border flex flex-row justify-start items-center gap-2 rounded-md mt-[2px]">
                              <Combobox
                                id="event-timezone"
                                aria-label={t('eventModal.selectTimezone')}
                                placeholder={t('eventModal.selectTimezone')}
                                className="w-[260px]"
                                value={formData?.eventLocalTimezone ?? null}
                                onValueChange={onTimezoneChange}
                                options={timezoneComboboxOptions}
                                emptyMessage={t('eventModal.noTimezones')}
                              />
                              <Button
                                {...clearFieldButtonProps}
                                aria-label={t('eventModal.clearTimezone')}
                                onClick={() => onTimezoneChange(undefined)}
                              >
                                <XCircleIcon weight="duotone" size={CLEAR_FIELD_ICON_SIZE} />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col items-start gap-2">
                      {formData?.date && (
                        <TpTooltip
                          content={
                            formData?.isTimeValid
                              ? t('eventModal.datetimeShown')
                              : t('eventModal.datetimeHidden')
                          }
                          placement="top"
                          delayDuration={0}
                          sideOffset={0}
                        >
                          <Checkbox
                            id="hide-datetime-from-presentation"
                            checked={!formData?.isTimeValid}
                            onCheckedChange={onTimeIsValidChange}
                            label={t('eventModal.hideDatetime')}
                            className="text-white/100 text-[14px] font-light tracking-wider"
                          />
                        </TpTooltip>
                      )}
                      <div className="whitespace-nowrap">
                      <Button
                        type="button"
                        size="md"
                        style="solid"
                        color="secondary"
                        background="inverse"
                        aria-label={formData?.show ? t('eventModal.hideEvent') : t('eventModal.showEvent')}
                        aria-pressed={!!formData?.show}
                        onClick={handleVisibilityClick}
                        leadingIcon={
                          formData?.show ? (
                            <EyeIcon weight="duotone" />
                          ) : (
                            <EyeSlashIcon weight="duotone" />
                          )
                        }
                      >
                        {formData?.show ? t('eventModal.eventShown') : t('eventModal.eventNotShown')}
                      </Button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-1 items-start w-full">
                  <label htmlFor="event-header-input" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">{t('eventModal.optionalHeader')}</label>
                  <Input
                    ref={headerInputRef}
                    id="event-header-input"
                    aria-label={t('eventModal.optionalHeader')}
                    disabled={!editingEnabled && !isLegacy}
                    name="slideSubTitle"
                    value={formData?.slideSubTitle ?? ''}
                    onChange={(e) => dispatchForm({ type: FormReducerActionType.SetField, payload: { slideSubTitle: e.target.value, isDirty: true } })}
                    size="md"
                    background="inverse"
                    className="inverse-form-surface box-border w-full"
                  />
                  <p className="m-0 p-0 text-[12px] font-light leading-4 text-white/60">
                    {t('eventModal.optionalHeaderHint')}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2 items-start w-full">
                <label htmlFor="event-title-input" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">{t('eventModal.title')}</label>
                <Input
                  ref={titleInputRef}
                  id="event-title-input"
                  disabled={!editingEnabled && !isLegacy}
                  name="slideTitle"
                  value={formData?.slideTitle === 'FROM SOURCE' ? '' : (formData?.slideTitle ?? '')}
                  onChange={(e) => dispatchForm({ type: FormReducerActionType.SetField, payload: { slideTitle: e.target.value, isDirty: true } })}
                  size="md"
                  background="inverse"
                  className="inverse-form-surface box-border w-full"
                />
              </div>
              <div className="flex flex-col gap-2 items-start w-full">
                <label htmlFor="event-description-editor" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">{t('eventModal.description')}</label>
                <Suspense fallback={<div>{t('eventModal.loadingEditor')}</div>}>
                  <MarkdownEditor
                    id="event-description-editor"
                    markdown={markdownValue}
                    onChange={handleMarkdownChange}
                    onBlurIn={handleMarkdownChangeFinal}
                    debounceMs={300}
                  />
                </Suspense>
              </div>
              {featureFlags?.courtroomMode && (
                <details className="flex flex-col gap-4 items-start w-full group">
                  <summary className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                    {t('eventModal.courtroomCopy')}
                  </summary>
                  <p className="text-white/60 text-sm m-0">
                    {t('eventModal.courtroomHint')}
                  </p>
                  <div className="flex flex-col gap-2 items-start w-full">
                    <label htmlFor="event-courtroom-title-input" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">{t('eventModal.courtroomHeadline')}</label>
                    <Input
                      id="event-courtroom-title-input"
                      disabled={!editingEnabled}
                      name="courtroomTitle"
                      value={formData?.courtroomTitle ?? ''}
                      onChange={(e) => dispatchForm({ type: FormReducerActionType.SetField, payload: { courtroomTitle: e.target.value, isDirty: true } })}
                      size="md"
                      background="inverse"
                      className="inverse-form-surface box-border w-full"
                    />
                  </div>
                  <div className="flex flex-col gap-2 items-start w-full">
                    <label htmlFor="event-courtroom-description-editor" className="text-white/100 font-light tracking-wider text-[14px] m-0 p-0">{t('eventModal.courtroomDescription')}</label>
                    <Suspense fallback={<div>{t('eventModal.loadingEditor')}</div>}>
                      <MarkdownEditor
                        id="event-courtroom-description-editor"
                        markdown={courtroomMarkdownValue}
                        onChange={handleCourtroomMarkdownChange}
                        debounceMs={300}
                      />
                    </Suspense>
                  </div>
                </details>
              )}
              <div className="flex w-full min-w-0 max-w-full flex-col">
                <EventModalSourcesSection
                  eventKey={currentEventIndex ?? 'new'}
                  initialSources={formData?.sources as MediaSource[]}
                  initialMedia={formData?.media as MediaSource[]}
                  onSourcesChange={handleUnifiedSourcesChanges}
                  caseId={caseId || ''}
                  isInModal={true}
                  editingEnabled={editingEnabled && !isLegacy}
                  sourcesFromChat={sourcesFromChat}
                  mediaSourcesFromChat={filteredMediaSourcesFromChat}
                  isTitleSlide={false}
                  layoutVariant="stacked"
                />
              </div>
            </div>
            </div>
        </Modal>
      ) : (
        <>
          {!isFullscreen && (events && events?.length > 0 && (formDataRef.current?.media ?? formData?.media)?.length) ? (
            // Not a dialog — a bare media view. It still needs the blur backdrop
            // that `Main` used to provide around this whole component; the two
            // dialog branches below now bring their own via `Modal`.
            <DismissibleBlurOverlay
              onClose={onClose}
              closeLabel={t('eventModal.closeEditor')}
              position="fixed"
            >
              <MediaDisplay
                mediaItem={(formDataRef.current?.media ?? formData?.media)?.[0]!}
                isTitleSlide={false}
              />
            </DismissibleBlurOverlay>
          ) : isResolvingOpenEvent ? (
            // The event list is being refetched (e.g. a background poll blanked it)
            // and the event we're editing isn't in the current list yet. It is
            // almost certainly coming back, so show a loading state rather than the
            // alarming "removed / no longer available" message.
            <Modal
              isOpen
              dismissOnEscape={false}
              dismissOnBackdropClick={false}
              onClose={onClose}
              size="sm"
              title={t('eventModal.loadingEvent')}
              bodyClassName="flex items-center justify-center py-4"
            >
              <HotKeys onCloseModal={onClose} isModal={true} />
              <Spinner size="md" color="white" variant="wave" label={t('eventModal.loadingEventSpinner')} />
            </Modal>
          ) : (
            <Modal
              isOpen
              dismissOnEscape={false}
              dismissOnBackdropClick={false}
              onClose={onClose}
              size="sm"
              title={t('eventModal.unavailable')}
              footer={
                <div className="flex w-full items-center justify-start">
                  <Button
                    type="button"
                    size="sm"
                    style="solid"
                    color="subdued"
                    background="default"
                    aria-label={t('eventModal.close')}
                    onClick={onClose}
                  >
                    {t('eventModal.close')}
                  </Button>
                </div>
              }
            >
              <HotKeys onCloseModal={onClose} isModal={true} />
              <p className="m-0 text-center text-white">
                {t('eventModal.unavailableBody')}
              </p>
            </Modal>
          )}
        </>
      )}
    </>
  );
};

EventModalComponent.displayName = 'EventModal';

/**
 * Memoized so unrelated `Main` re-renders don't cascade into the modal while
 * it's open. Effective because `Main` passes stable callbacks
 * (`useStableCallback`), a memoized `eventsFromChat` array, and stable
 * Toast/FullScreen context values; flags/tokens are read via reactive hooks.
 */
const EventModal = React.memo(EventModalComponent);

export default EventModal;
